import apiClient from './api';
import publicApiClient from './publicApi';
import { mockApiService } from './mockApiService';
import { shouldUseMockAPI } from '../config/apiConfig';

// Authentication Service
export const authService = {
  // Đăng nhập với username/password
  login: async (username, password) => {
    try {
      console.log('Attempting to login with:', { username, password });
      
      // Check if should use mock API
      if (shouldUseMockAPI()) {
        console.log('Using mock API for login');
        const mockResponse = await mockApiService.login(username, password);
        
        if (mockResponse.token) {
          localStorage.setItem('token', mockResponse.token);
          localStorage.setItem('user', JSON.stringify(mockResponse.user));
        }
        
        return mockResponse;
      }
      
      // Sử dụng public API client cho login (không cần authentication)
      // Backend expects `UserName` and `Password` properties
      const requestBody = {
        UserName: username,
        Password: password
      };
      
      console.log('Sending login request to:', publicApiClient.defaults.baseURL + '/Account/Login');
      console.log('Request body:', { UserName: username, Password: '***' });
      
      const response = await publicApiClient.post('/Account/Login', requestBody);
      
      console.log('Login successful:', response.data);
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      console.error('Request config:', error.config);
      
      // Extract detailed error message
      let errorMessage = error.message;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }
      
      // Nếu backend chưa sẵn sàng hoặc lỗi 401, sử dụng mock API
      if (error.response?.status === 404 || error.response?.status === 401) {
        console.log('Backend not ready or requires auth, using mock API for login');
        const mockResponse = await mockApiService.login(username, password);
        
        if (mockResponse.token) {
          localStorage.setItem('token', mockResponse.token);
          localStorage.setItem('user', JSON.stringify(mockResponse.user));
        }
        
        return mockResponse;
      }
      
      // For 400 errors, throw the actual error message from backend
      if (error.response?.status === 400) {
        throw new Error(errorMessage || 'Invalid login credentials or request format');
      }
      
      throw new Error(errorMessage || 'Login failed');
    }
  },

  // Đăng ký user mới
  register: async (userData) => {
    try {
      console.log('Attempting to register with data:', userData);
      
      // Check if should use mock API
      if (shouldUseMockAPI()) {
        console.log('Using mock API for registration');
        return await mockApiService.register(userData);
      }
      
      // Backend expects `UserName`, `Email`, and `Password` fields
      const requestBody = {
        UserName: userData.username || userData.user_name,
        Email: userData.email,
        Password: userData.password
      };
      
      console.log('Sending registration request to:', publicApiClient.defaults.baseURL + '/Account/Register');
      console.log('Request body:', { UserName: requestBody.UserName, Email: requestBody.Email, Password: '***' });
      
      // Sử dụng public API client không cần authentication
      const response = await publicApiClient.post('/Account/Register', requestBody);
      console.log('Registration successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Nếu backend chưa sẵn sàng hoặc lỗi 401, sử dụng mock API
      if (error.response?.status === 404 || error.response?.status === 401) {
        console.log('Backend not ready or requires auth, using mock API for registration');
        return await mockApiService.register(userData);
      }
      throw error.response?.data || error.message;
    }
  },

  // Google Login
  // Backend expects `IdToken` property
  googleLogin: async (googleToken) => {
    try {
      console.log('Attempting Google login with token:', googleToken ? 'Token provided' : 'No token');
      
      const payload = {
        IdToken: googleToken
      };

      // Use publicApiClient since Google login doesn't require authentication
      const response = await publicApiClient.post('/Account/GoogleLogin', payload);

      console.log('Google login successful:', response.data);

      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      console.error('Google login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error.response?.data || error.message;
    }
  },

  // Get user profile
  getProfile: async () => {
    try {
      const response = await apiClient.get('/Account/Profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Đăng xuất
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Check if user is logged in
  isLoggedIn: () => {
    return !!localStorage.getItem('token');
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Kiểm tra token có hợp lệ không (alias for isLoggedIn)
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};
